import { AlertTriangle, CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useAutoConfigureInit, useAutoConfigureStatus } from '@/api/hooks/use-auto-configure'

interface AutoConfigureCardProps {
  domainId: string
  onComplete: () => void
}

type CardState = 'idle' | 'waiting' | 'complete' | 'failed' | 'unsupported'

const CARD = 'rounded-xl border border-surface-3 bg-surface-1 p-5'
const PROVIDER_BTN =
  'inline-flex items-center gap-2 rounded-lg border border-surface-3 bg-surface-0 px-4 py-2.5 text-sm font-medium text-text-primary hover:bg-surface-1 hover:border-surface-4 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm'

function CloudflareLogo(): React.JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Cloudflare"
    >
      <path
        d="M44.044 40.791l1.478-5.093c.288-.993.169-1.907-.335-2.577-.468-.626-1.24-.998-2.116-1.044l-18.794-.254a.413.413 0 01-.34-.181.442.442 0 01-.056-.386c.071-.22.28-.374.513-.386l19.016-.254c2.27-.115 4.735-1.96 5.595-4.2l1.088-2.834a.787.787 0 00.043-.37C48.66 15.47 41.777 9.334 33.547 9.334c-7.274 0-13.458 4.838-15.484 11.477-.909-.685-2.058-1.09-3.31-1.033-2.348.114-4.239 2.019-4.35 4.37a4.558 4.558 0 00.114 1.352C6.43 25.717 3 29.28 3 33.635c0 .597.057 1.182.157 1.752.057.288.3.494.592.494h39.704c.277 0 .52-.19.591-.462z"
        fill="#F38020"
      />
      <path
        d="M49.156 25.39c-.19 0-.38.012-.57.023-.142.012-.267.103-.325.237l-1.05 2.777c-.289.994-.17 1.908.334 2.578.468.626 1.24.997 2.116 1.044l4.066.254c.214.012.38.08.34.181a.442.442 0 01-.056.386c-.07.22-.28.374-.513.386l-4.288.254c-2.282.115-4.735 1.96-5.595 4.2l-.305.785c-.091.237.08.474.335.474H60.41c.243 0 .46-.168.52-.405A11.367 11.367 0 0061.5 35.33c0-5.487-4.457-9.94-9.956-9.94h-2.388z"
        fill="#FAAE40"
      />
    </svg>
  )
}

function VercelLogo(): React.JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 76 65" fill="currentColor" aria-label="Vercel">
      <path d="M37.5274 0L75.0548 65H0L37.5274 0Z" />
    </svg>
  )
}

export function AutoConfigureCard({
  domainId,
  onComplete,
}: AutoConfigureCardProps): React.JSX.Element {
  const [state, setState] = useState<CardState>('idle')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [providerName, setProviderName] = useState<string | null>(null)
  const [recordsCreated, setRecordsCreated] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null)

  const initMutation = useAutoConfigureInit()
  const statusQuery = useAutoConfigureStatus(domainId, sessionId, state === 'waiting')

  const handleComplete = useCallback(() => {
    setState('complete')
    onComplete()
  }, [onComplete])

  useEffect(() => {
    const handler = (e: MessageEvent): void => {
      if (e.data?.type === 'auto-configure-complete') handleComplete()
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [handleComplete])

  useEffect(() => {
    if (!statusQuery.data) return
    const { status, records_created, error_message } = statusQuery.data
    if (status === 'complete') {
      setRecordsCreated(records_created)
      handleComplete()
    } else if (status === 'failed') {
      setErrorMessage(error_message ?? 'Configuration failed. Please try again.')
      setState('failed')
    }
  }, [statusQuery.data, handleComplete])

  function handleStartProvider(provider: 'auto' | 'cloudflare' | 'vercel'): void {
    initMutation.mutate(
      { domainId, provider },
      {
        onSuccess: (data) => {
          setSessionId(data.session_id)
          setProviderName(data.provider_name)
          const popup = window.open(data.auth_url, 'dns-config', 'width=600,height=700,popup=yes')
          if (!popup) setFallbackUrl(data.auth_url)
          setState('waiting')
        },
        onError: (err: unknown) => {
          const error = err as { code?: string; message?: string }
          if (error.code === 'FEATURE_NOT_AVAILABLE') {
            setState('unsupported')
            return
          }
          setErrorMessage(error.message ?? 'Failed to start auto-configuration.')
          setState('failed')
        },
      },
    )
  }

  function handleRetry(): void {
    setErrorMessage(null)
    setSessionId(null)
    setFallbackUrl(null)
    setState('idle')
  }

  if (state === 'idle')
    return (
      <div className={CARD}>
        <h3 className="text-sm font-semibold text-text-primary">One-click DNS setup</h3>
        <p className="mt-1 text-xs text-text-secondary">
          Auto-configure is coming soon. For now, add the records manually below.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            disabled
            className={`${PROVIDER_BTN} opacity-50 cursor-not-allowed`}
            title="Cloudflare auto-configure coming soon"
          >
            <CloudflareLogo />
            <span>Cloudflare</span>
            <span className="text-[10px] text-text-tertiary font-normal">soon</span>
          </button>
          <button
            type="button"
            onClick={() => handleStartProvider('vercel')}
            disabled={initMutation.isPending}
            className={PROVIDER_BTN}
          >
            {initMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <VercelLogo />}
            Vercel
          </button>
        </div>
        <p className="mt-2 text-xs text-text-tertiary">
          Cloudflare, Vercel, GoDaddy, and more — launching soon.
        </p>
      </div>
    )

  if (state === 'waiting')
    return (
      <div className={CARD}>
        <h3 className="text-sm font-semibold text-text-primary">Configuring DNS</h3>
        <div className="mt-2 flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
          <span className="text-sm text-text-secondary">Waiting for authorization...</span>
        </div>
        <p className="mt-1 text-xs text-text-tertiary">
          Complete the authorization in the popup window. This page will update automatically.
        </p>
        {fallbackUrl && (
          <a
            href={fallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-xs font-medium text-brand-600 hover:text-brand-700 underline"
          >
            Popup blocked? Open authorization page
          </a>
        )}
      </div>
    )

  if (state === 'complete')
    return (
      <div className={CARD}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <h3 className="text-sm font-semibold text-text-primary">DNS Configured</h3>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          {recordsCreated} record{recordsCreated !== 1 ? 's' : ''} configured
          {providerName ? ` via ${providerName}` : ''}
        </p>
      </div>
    )

  if (state === 'unsupported')
    return (
      <div className={CARD}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-text-tertiary" />
          <h3 className="text-sm font-semibold text-text-primary">Auto-configure not available</h3>
        </div>
        <p className="mt-1 text-xs text-text-secondary">
          Your DNS provider doesn't support Domain Connect. If you use Vercel DNS, try that instead.
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleStartProvider('vercel')}
            disabled={initMutation.isPending}
            className={PROVIDER_BTN}
          >
            {initMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Try Vercel
          </button>
          <span className="text-xs text-text-tertiary">or add records manually below</span>
        </div>
      </div>
    )

  return (
    <div className={CARD}>
      <div className="flex items-center gap-2">
        <XCircle className="h-5 w-5 text-red-500" />
        <h3 className="text-sm font-semibold text-text-primary">Auto-configure didn't work</h3>
      </div>
      <p className="mt-2 text-sm text-text-secondary">
        {errorMessage ??
          "This usually means the domain isn't managed by the selected DNS provider."}
      </p>
      <ul className="mt-2 text-xs text-text-tertiary list-disc pl-4 space-y-1">
        <li>
          For <strong>Vercel</strong>: the domain must be added to a Vercel project and use Vercel's
          nameservers
        </li>
        <li>
          If your DNS is on <strong>Cloudflare</strong>, GoDaddy, or another provider, use the
          manual setup below
        </li>
      </ul>
      <div className="mt-4 flex items-center gap-3">
        <button type="button" onClick={handleRetry} className={PROVIDER_BTN}>
          Try again
        </button>
        <button
          type="button"
          onClick={handleRetry}
          className="text-sm text-text-secondary hover:text-text-primary transition-colors"
        >
          Set up manually instead
        </button>
      </div>
    </div>
  )
}
